// ===================================================================
// RELATÓRIOS - Admin
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import {
  formatDate,
  exportToCSV,
  downloadBlobFile,
  buildReportExportFilename,
  printWithDocumentTitle,
} from '../../utils/helpers';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { isOrdemInscricaoMode } from '../../utils/selectionMode';

const EXEMPTION_LABELS = {
  approved: 'Deferido',
  rejected: 'Indeferido',
  pending: 'Pendente',
  analyzing: 'Em análise',
  appealed: 'Em recurso',
  appeal_approved: 'Deferido',
  appeal_rejected: 'Indeferido',
  not_analyzed: 'Não analisado',
};

const isInstitutionImageUrl = (url) => url && (url.startsWith('http') || url.startsWith('/'));

const ReportHeader = ({ config, title, processName }) => {
  const hasLeft = isInstitutionImageUrl(config.institution_icon);
  const hasRight = isInstitutionImageUrl(config.institution_icon_right);
  const logoSlot = { width: 80, flexShrink: 0, minHeight: 80, display: 'flex', alignItems: 'center' };
  const imgStyle = { width: 80, height: 80, objectFit: 'contain' };

  const titleBlock = (
    <>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}>{title}</h2>
      {processName && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>{processName}</p>}
      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9CA3AF' }}>
        Gerado em {formatDate(new Date().toISOString())}
      </p>
    </>
  );

  if (!hasLeft && !hasRight) {
    return (
      <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #E5E7EB' }}>
        {titleBlock}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #E5E7EB' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ ...logoSlot, justifyContent: 'flex-start' }}>
          {hasLeft && <img src={config.institution_icon} alt="" style={imgStyle} />}
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>{titleBlock}</div>
        <div style={{ ...logoSlot, justifyContent: 'flex-end' }}>
          {hasRight && <img src={config.institution_icon_right} alt="" style={imgStyle} />}
        </div>
      </div>
    </div>
  );
};

const DEFAULT_TITLES = {
  history: 'Histórico de alterações por candidato',
  candidate_full_contact: 'Consulta — dados completos e contato (uso interno)',
  exemption_before: 'Resultado Preliminar dos pedidos de isenção Deferidos e Indeferidos',
  exemption_after: 'Resultado dos pedidos de isenção Deferidos e Indeferidos após recurso',
  special_attendance_before: 'Resultado preliminar dos pedidos de atendimento especializado/específico',
  special_attendance_after: 'Resultado definitivo dos pedidos de atendimento especializado/específico',
  prova_preliminary_enrolled: 'Lista preliminar de candidatos inscritos',
  prova_homologation_enrollments: 'Homologação das inscrições',
  prova_homologated_exam_places: 'Lista de inscritos homologados com local e sala de prova (divulgação geral)',
  prova_homologated_exam_by_room: 'Lista por local/sala — uso na porta (CPF mascarado)',
  prova_presence_by_room: 'Lista de presença por local/sala (uso interno — CPF completo)',
  preliminary_list: 'Lista Preliminar de inscrições Deferidas e Indeferidas',
  final_approved_list: 'Homologação do Resultado das Inscrições Deferidas após recurso',
  classification_preliminary: 'Resultado Preliminar',
  classification_final: 'Resultado Final',
  prova_gabarito_preliminar: 'Gabarito preliminar',
  prova_gabarito_definitivo: 'Gabarito definitivo',
  prova_resultado_preliminar: 'Resultado preliminar',
  prova_resultado_final: 'Resultado final',
  prova_lista_presenca_interno: 'Lista de presença (interno)',
  prova_convocacao_matricula_interno: 'Lista de convocação para matrícula (interno)',
  prova_lista_espera_cota_interno: 'Lista de espera (interno)',
  prova_questionario_socioeconomico: 'Questionário sócio econômico',
  homologation_signatures: 'Lista de presença (interno)',
  enrollment_data: 'Lista de convocação para matrícula (interno)',
  waiting_list_by_quota: 'Lista de espera (interno)',
  questionnaire_socioeconomic: 'Questionário socioeconômico',
};

/** Análise curricular — admin: numeração 1..12 (sem 2C/2D, específicos de prova). */
const CURRICULAR_ADMIN_REPORT_OPTIONS = [
  { value: 'history', label: '1. Histórico de alterações por candidato' },
  { value: 'candidate_full_contact', label: '2. Consulta — dados completos e contato (todos os inscritos)' },
  { value: 'exemption_before', label: '3. Resultado preliminar dos pedidos de isenção deferidos e indeferidos' },
  { value: 'exemption_after', label: '4. Resultado dos pedidos de isenção deferidos e indeferidos após recurso' },
  { value: 'preliminary_list', label: '5. Lista preliminar de inscrições deferidas e indeferidas' },
  { value: 'final_approved_list', label: '6. Homologação do resultado das inscrições deferidas após recurso' },
  { value: 'classification_preliminary', label: '7. Resultado preliminar' },
  { value: 'classification_final', label: '8. Resultado final' },
  { value: 'homologation_signatures', label: '9. Lista de presença (interno)' },
  { value: 'enrollment_data', label: '10. Lista de convocação para matrícula (interno)' },
  { value: 'waiting_list_by_quota', label: '11. Lista de espera (interno)' },
  { value: 'questionnaire_socioeconomic', label: '12. Questionário socioeconômico' },
];

/** Modo prova — admin: relatórios 1–19 (16–19 = mesma lógica dos itens 9–12 do modo curricular). */
const PROVA_ADMIN_REPORT_OPTIONS = [
  { value: 'history', label: '1. Histórico de alterações por candidato' },
  { value: 'candidate_full_contact', label: '2. Consulta — dados completos e contato (todos os inscritos)' },
  { value: 'exemption_before', label: '3. Resultado preliminar dos pedidos de isenção deferidos e indeferidos' },
  { value: 'exemption_after', label: '4. Resultado dos pedidos de isenção deferidos e indeferidos após recurso' },
  { value: 'special_attendance_before', label: '5. Resultado preliminar dos pedidos de atendimento especializado/específico' },
  { value: 'special_attendance_after', label: '6. Resultado definitivo dos pedidos de atendimento especializado/específico' },
  { value: 'prova_preliminary_enrolled', label: '7. Lista preliminar de candidatos inscritos' },
  { value: 'prova_homologation_enrollments', label: '8. Homologação das inscrições' },
  { value: 'prova_homologated_exam_places', label: '9. Lista de inscritos homologados com local e sala (divulgação geral)' },
  { value: 'prova_homologated_exam_by_room', label: '10. Lista por local/sala — porta da sala (CPF mascarado)' },
  { value: 'prova_presence_by_room', label: '11. Lista de presença por local/sala (CPF completo + assinatura)' },
  { value: 'prova_gabarito_preliminar', label: '12. Gabarito preliminar' },
  { value: 'prova_gabarito_definitivo', label: '13. Gabarito definitivo' },
  { value: 'prova_resultado_preliminar', label: '14. Resultado preliminar' },
  { value: 'prova_resultado_final', label: '15. Resultado final' },
  { value: 'prova_lista_presenca_interno', label: '16. Lista de presença (interno)' },
  { value: 'prova_convocacao_matricula_interno', label: '17. Lista de convocação para matrícula (interno)' },
  { value: 'prova_lista_espera_cota_interno', label: '18. Lista de espera (interno)' },
  { value: 'prova_questionario_socioeconomico', label: '19. Questionário sócio econômico' },
];

const PROVA_GABARITO_REPORT_TYPES = ['prova_gabarito_preliminar', 'prova_gabarito_definitivo'];

/** Prova 16–19 reutilizam API e layout dos relatórios curriculares 9–12. */
const PROVA_INTERNAL_REPORT_MAP = {
  prova_lista_presenca_interno: 'homologation_signatures',
  prova_convocacao_matricula_interno: 'enrollment_data',
  prova_lista_espera_cota_interno: 'waiting_list_by_quota',
  prova_questionario_socioeconomico: 'questionnaire_socioeconomic',
};

const resolveReportApiType = (reportType) => PROVA_INTERNAL_REPORT_MAP[reportType] || reportType;

const PROVA_CLASSIFICATION_REPORT_TYPES = ['prova_resultado_preliminar', 'prova_resultado_final'];

const CURRICULAR_ADMIN_REPORT_VALUES = CURRICULAR_ADMIN_REPORT_OPTIONS.map((o) => o.value);
const PROVA_ADMIN_REPORT_VALUES = PROVA_ADMIN_REPORT_OPTIONS.map((o) => o.value);

/** Perfil campus — numeração 1..6 (independente da modalidade do processo). */
const CAMPUS_REPORT_OPTIONS = [
  { value: 'candidate_full_contact', label: '1. Consulta — dados completos e contato (todos os inscritos)' },
  { value: 'classification_final', label: '2. Resultado final' },
  { value: 'homologation_signatures', label: '3. Lista de presença (interno)' },
  { value: 'enrollment_data', label: '4. Lista de convocação para matrícula (interno)' },
  { value: 'waiting_list_by_quota', label: '5. Lista de espera (interno)' },
  { value: 'questionnaire_socioeconomic', label: '6. Questionário socioeconômico' },
];

const CAMPUS_REPORT_VALUES = CAMPUS_REPORT_OPTIONS.map((o) => o.value);

const REPORT_TABLE_LAYOUT = {
  table: { width: '100%', fontSize: 13, borderCollapse: 'collapse', tableLayout: 'fixed' },
  /** Lista de espera (interno): muitas colunas dinâmicas — não usar fixed + % (esmaga Nome/CPF). */
  waitingListTable: {
    width: 'max-content',
    minWidth: '100%',
    fontSize: 13,
    borderCollapse: 'collapse',
    tableLayout: 'auto',
  },
  /** Relatório 1B: caber em A4 paisagem — larguras fixas em %, texto quebra em várias linhas. */
  contactTable: { width: '100%', maxWidth: '100%', fontSize: 10, borderCollapse: 'collapse', tableLayout: 'fixed' },
  contactColPct: ['4%', '11%', '8%', '10%', '6%', '5%', '9%', '9%', '10%', '28%'],
  waitingListThTd: {
    base: { padding: '8px 10px', border: '1px solid #E5E7EB', verticalAlign: 'top' },
    id: { whiteSpace: 'nowrap', minWidth: 64 },
    num: { whiteSpace: 'nowrap', minWidth: 48 },
    name: { minWidth: 200, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' },
    cpf: { whiteSpace: 'nowrap', minWidth: 128 },
    score: { whiteSpace: 'nowrap', minWidth: 56 },
    quotaInsc: { minWidth: 100, whiteSpace: 'normal', wordBreak: 'break-word' },
    quotaCode: { minWidth: 52, maxWidth: 96, whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 11, lineHeight: 1.2 },
  },
  col: { checkbox: 40, num: 52, id: 52, name: '40%', cpf: 118, signature: 180, course: 140, quota: 90, score: 70, chamada: 80, quotaCode: 90 },
  enrollmentCol: { checkbox: '3%', id: '5%', num: '4%', name: '22%', cpf: '11%', course: '14%', quotaInsc: '9%', quotaClass: '9%', score: '5%', chamada: '5%' },
  listCol: { num: '5%', name: '20%', cpf: '11%', campus: '9%', course: '16%', quota: '7%', score: '6%', situacao: '9%', motivo: '12%' },
  /** Relatórios 10/11 por sala: colunas compactas (Nº, inscrição, CPF) para liberar espaço à assinatura. */
  roomPresenceCol: { num: 40, inscription: 72, cpf: 118, name: '18%', campus: '10%', course: '14%', signature: '26%', signatureMin: 200 },
  classificationCol: { num: '5%', name: '20%', cpf: '11%', campus: '9%', course: '14%', quotaInsc: '9%', score: '7%', situacao: '9%', quotaClass: '10%' },
  /** Resultado preliminar/final (modo prova): inclui Obj./Red. — larguras somam 100%. */
  provaClassificationTable: { width: '100%', fontSize: 11, borderCollapse: 'collapse', tableLayout: 'fixed' },
  provaClassificationColEssay: {
    pos: '4%', num: '4%', name: '24%', cpf: '9%', campus: '7%', course: '10%',
    quotaInsc: '8%', notaObj: '6%', notaRed: '6%', total: '6%', situacao: '8%', quotaClass: '8%',
  },
  provaClassificationColNoEssay: {
    pos: '4%', num: '4%', name: '30%', cpf: '10%', campus: '8%', course: '12%',
    quotaInsc: '8%', total: '6%', situacao: '9%', quotaClass: '9%',
  },
  provaClassificationCell: { padding: '6px 7px', border: '1px solid #E5E7EB', verticalAlign: 'top' },
  provaClassificationTh: { padding: '6px 7px', border: '1px solid #E5E7EB', verticalAlign: 'bottom', lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' },
};

const STATUS_LABELS = {
  approved: 'Deferido',
  rejected: 'Indeferido',
  pending: 'Pendente',
  not_analyzed: 'Não analisado',
  appealed: 'Em recurso',
  appeal_approved: 'Deferido',
  appeal_rejected: 'Indeferido'
};

/** Visualização do relatório 12/13 — PDF no layout da folha OMR. */
const GabaritoReportPreview = ({ reportData, config, customTitle, processName, error }) => {
  const blobUrlRef = useRef(null);

  const pdfDownloadName = (reportData?.pdf_filename || 'gabarito.pdf').trim() || 'gabarito.pdf';

  const { pdfBlob, pdfUrl } = useMemo(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    const b64 = reportData?.pdf_base64;
    if (!b64) return { pdfBlob: null, pdfUrl: null };
    try {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      return { pdfBlob: blob, pdfUrl: url };
    } catch {
      return { pdfBlob: null, pdfUrl: null };
    }
  }, [reportData?.pdf_base64]);

  const pdfServerUrl =
    reportData?.process_id != null
      ? apiClient.getReportProvaGabaritoPdfUrl(reportData.process_id, reportData.phase)
      : null;

  useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    },
    []
  );

  if (!reportData || reportData.report_kind !== 'gabarito') {
    return (
      <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
        {error || 'Carregue o relatório ou verifique se o gabarito está salvo em Processos → Gabarito.'}
      </p>
    );
  }

  const labels = reportData.labels || ['A'];
  const reportTypeKey =
    reportData.phase === 'definitive' ? 'prova_gabarito_definitivo' : 'prova_gabarito_preliminar';
  const title =
    customTitle.trim() ||
    (reportData.pdf_filename || '').replace(/\.pdf$/i, '') ||
    DEFAULT_TITLES[reportTypeKey] ||
    `Gabarito ${reportData.phase_label || ''}`;

  return (
    <>
      <ReportHeader config={config} title={title} processName={reportData.process_name || processName} />
      <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.55, margin: '0 0 12px' }}>
        Mesmo layout da <strong>folha de respostas</strong> (colunas e bolhas). Respostas corretas aparecem{' '}
        <strong>preenchidas</strong>
        {labels.length > 1 ? (
          <>
            {' '}
            — {reportData.pdf_page_count || labels.length} página(s), uma por tipo de folha ({labels.join(', ')}).
          </>
        ) : (
          '.'
        )}{' '}
        Use para divulgação oficial ou conferência pelo candidato.
      </p>
      {pdfUrl ? (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }} className="no-print">
            <button
              type="button"
              onClick={() => pdfBlob && downloadBlobFile(pdfBlob, pdfDownloadName)}
              disabled={!pdfBlob}
              style={{ ...styles.button, ...styles.buttonPrimary, padding: '8px 14px' }}
            >
              Baixar PDF
            </button>
            <a
              href={pdfServerUrl || pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 14px', textDecoration: 'none' }}
            >
              Abrir em nova aba
            </a>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Nome do arquivo: {pdfDownloadName}</span>
          </div>
          <iframe
            title="Gabarito oficial"
            src={pdfServerUrl || pdfUrl}
            style={{
              width: '100%',
              minHeight: '72vh',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              background: '#F9FAFB',
            }}
          />
        </>
      ) : (
        <p style={{ color: '#B45309', fontSize: 13 }}>Não foi possível montar a visualização do PDF.</p>
      )}
    </>
  );
};

export const ReportsManager = React.memo(({ userRole = 'admin' }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [config, setConfig] = useState({ institution_name: 'IFNMG', institution_icon: '', institution_icon_right: '' });
  const isCampus = userRole === 'campus';
  const [reportType, setReportType] = useState(() => (userRole === 'campus' ? 'classification_final' : 'history'));
  const [customTitle, setCustomTitle] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [enrollmentFormat, setEnrollmentFormat] = useState('resumo');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [campusFilter, setCampusFilter] = useState('all');
  const [showSignatureList, setShowSignatureList] = useState(false);
  const [printOrientation, setPrintOrientation] = useState('portrait');
  const [questionnaireScope, setQuestionnaireScope] = useState('todos');
  const [questionnaireFormat, setQuestionnaireFormat] = useState('resumido');
  const [questionnaireIdentify, setQuestionnaireIdentify] = useState(true);
  const [questionnaireSearch, setQuestionnaireSearch] = useState('');
  const [questionnaireSearchSubmitted, setQuestionnaireSearchSubmitted] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contactSearchSubmitted, setContactSearchSubmitted] = useState('');

  const selectedProcess = useMemo(
    () => processes.find((p) => String(p.id) === String(selectedProcessId)),
    [processes, selectedProcessId]
  );
  const selectionMode = selectedProcess?.selection_mode || 'analise_curricular';
  const isProvaProcess = selectionMode === 'prova';
  const isOrdemProcess = isOrdemInscricaoMode(selectionMode);
  const processUsesQuotas = selectedProcess?.use_quotas === true;

  const reportSelectOptions = useMemo(() => {
    if (isCampus) return CAMPUS_REPORT_OPTIONS;
    return isProvaProcess ? PROVA_ADMIN_REPORT_OPTIONS : CURRICULAR_ADMIN_REPORT_OPTIONS;
  }, [isCampus, isProvaProcess]);

  const effectiveReportType = useMemo(() => {
    if (!selectedProcessId) {
      const v = CURRICULAR_ADMIN_REPORT_VALUES;
      return v.includes(reportType) ? reportType : v[0];
    }
    const valid = isCampus ? CAMPUS_REPORT_VALUES : isProvaProcess ? PROVA_ADMIN_REPORT_VALUES : CURRICULAR_ADMIN_REPORT_VALUES;
    if (valid.includes(reportType)) return reportType;
    if (isCampus && valid.includes('classification_final')) return 'classification_final';
    return valid[0];
  }, [selectedProcessId, selectionMode, isCampus, isProvaProcess, reportType]);

  const apiReportType = useMemo(
    () => resolveReportApiType(effectiveReportType),
    [effectiveReportType],
  );

  const isListReport56 =
    effectiveReportType === 'preliminary_list' || effectiveReportType === 'final_approved_list';
  const showListReportNota = isListReport56 && !isOrdemProcess;
  const showListReportQuota = !isListReport56 || processUsesQuotas;

  const isClassificationReport =
    effectiveReportType === 'classification_preliminary' || effectiveReportType === 'classification_final';
  const showClassificationNota =
    isClassificationReport && !isOrdemProcess && !PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType);
  const showClassificationScoreCol =
    PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType) || showClassificationNota;
  const showClassificationQuota =
    PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType) || (isClassificationReport && processUsesQuotas);

  const showEnrollmentNota = !isOrdemProcess;
  const showEnrollmentQuota = processUsesQuotas;

  const isRoomSectionReport = effectiveReportType === 'prova_homologated_exam_by_room' || effectiveReportType === 'prova_presence_by_room';

  useEffect(() => {
    if (reportType !== effectiveReportType) setReportType(effectiveReportType);
  }, [reportType, effectiveReportType]);

  useEffect(() => {
    if (PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType)) {
      setPrintOrientation('landscape');
    }
  }, [effectiveReportType]);

  useEffect(() => {
    const styleId = 'report-print-orientation';
    let el = document.getElementById(styleId);
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    const size = printOrientation === 'landscape' ? 'A4 landscape' : 'A4 portrait';
    const roomBreak =
      effectiveReportType === 'prova_homologated_exam_by_room' || effectiveReportType === 'prova_presence_by_room'
        ? ' .report-prova-room-block { page-break-after: always; } .report-prova-room-sections > .report-prova-room-block:last-of-type { page-break-after: auto; }'
        : '';
    const provaClassPrint = PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType)
      ? ' .report-prova-classification-table th, .report-prova-classification-table td { padding: 4px 5px !important; font-size: 10px !important; line-height: 1.2; } .report-prova-classification-table .report-col-score, .report-prova-classification-table .report-col-num { white-space: nowrap !important; } .report-prova-classification-table .report-col-score { text-align: right !important; }'
      : '';
    el.textContent = `@media print { @page { size: ${size}; margin: 15mm; } .no-print { display: none !important; } .report-content.print-signature-only .report-homologation-full { display: none !important; }${roomBreak}${provaClassPrint} }`;
    return () => {
      const s = document.getElementById(styleId);
      if (s) s.remove();
    };
  }, [printOrientation, effectiveReportType]);

  useEffect(() => {
    apiClient.getConfig().then((d) => setConfig({
      institution_name: d.institution_name || 'IFNMG',
      institution_icon: d.institution_icon || '',
      institution_icon_right: d.institution_icon_right || '',
    })).catch(() => {});
  }, []);

  const loadReport = useCallback(async (searchOverride = '') => {
    if (!selectedProcessId) return;
    setLoading(true);
    setError('');
    const search = searchOverride;
    const rt = effectiveReportType;
    const apiRt = resolveReportApiType(rt);
    try {
      let result;
      if (rt === 'history') {
        result = await apiClient.getReportHistoryChanges(selectedProcessId, search);
      } else if (apiRt === 'homologation_signatures') {
        result = await apiClient.getReportHomologationSignatures(selectedProcessId);
      } else if (apiRt === 'enrollment_data') {
        result = await apiClient.getReportEnrollmentData(selectedProcessId, enrollmentFormat);
      } else if (apiRt === 'waiting_list_by_quota') {
        result = await apiClient.getReportWaitingListByQuota(selectedProcessId);
      } else if (apiRt === 'candidate_full_contact') {
        result = await apiClient.getReportCandidatesFullContact(selectedProcessId, contactSearchSubmitted);
      } else if (apiRt === 'questionnaire_socioeconomic') {
        result = await apiClient.getReportQuestionnaireSocioeconomic(selectedProcessId, {
          scope: questionnaireScope,
          format: questionnaireFormat,
          identify: questionnaireIdentify,
          search: questionnaireFormat === 'por_candidato' ? questionnaireSearchSubmitted : undefined
        });
      } else {
        let fn;
        if (rt === 'exemption_before') fn = apiClient.getReportExemptionBeforeAppeal;
        else if (rt === 'exemption_after') fn = apiClient.getReportExemptionAfterAppeal;
        else if (rt === 'special_attendance_before') fn = apiClient.getReportSpecialAttendanceBeforeAppeal;
        else if (rt === 'special_attendance_after') fn = apiClient.getReportSpecialAttendanceAfterAppeal;
        else if (rt === 'prova_preliminary_enrolled') fn = apiClient.getReportProvaPreliminaryEnrolled;
        else if (rt === 'prova_homologation_enrollments') fn = apiClient.getReportProvaHomologationEnrollments;
        else if (rt === 'prova_homologated_exam_places') fn = apiClient.getReportProvaHomologatedExamPlaces;
        else if (rt === 'prova_homologated_exam_by_room') fn = apiClient.getReportProvaHomologatedExamByRoom;
        else if (rt === 'prova_presence_by_room') fn = apiClient.getReportProvaPresenceByRoom;
        else if (rt === 'prova_gabarito_preliminar') {
          result = await apiClient.getReportProvaGabarito(selectedProcessId, 'preliminary');
          setData(result?.report_kind === 'gabarito' ? [result] : []);
          setLoading(false);
          return;
        } else if (rt === 'prova_gabarito_definitivo') {
          result = await apiClient.getReportProvaGabarito(selectedProcessId, 'definitive');
          setData(result?.report_kind === 'gabarito' ? [result] : []);
          setLoading(false);
          return;
        } else if (rt === 'preliminary_list') fn = apiClient.getReportPreliminaryList;
        else if (rt === 'final_approved_list') fn = apiClient.getReportFinalApprovedList;
        else if (rt === 'prova_resultado_preliminar') fn = apiClient.getReportProvaResultadoPreliminar;
        else if (rt === 'prova_resultado_final') fn = apiClient.getReportProvaResultadoFinal;
        else if (rt === 'classification_final') fn = apiClient.getReportClassificationFinal;
        else if (rt === 'classification_preliminary') fn = apiClient.getReportClassificationPreliminary;
        else fn = apiClient.getReportClassificationPreliminary;
        result = await fn(selectedProcessId);
      }
      if (result && Array.isArray(result.groups)) {
        setData(result.groups);
        if (apiRt === 'homologation_signatures' || apiRt === 'enrollment_data') {
          const ids = new Set();
          result.groups.forEach((grp) => grp.courses?.forEach((cr) => cr.rows?.forEach((r) => r.application_id && ids.add(r.application_id))));
          setSelectedIds(ids);
          setShowSignatureList(false);
        }
      } else if (result && Array.isArray(result.sections)) {
        setData(result.sections);
      } else if (Array.isArray(result)) {
        setData(result);
      } else if (apiRt === 'questionnaire_socioeconomic' && result) {
        setData([result]);
      } else {
        setData([]);
      }
    } catch (err) {
      setError(err.message || 'Erro ao carregar relatório');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProcessId, effectiveReportType, enrollmentFormat, questionnaireScope, questionnaireFormat, questionnaireIdentify, questionnaireSearchSubmitted, contactSearchSubmitted]);

  useEffect(() => {
    if (selectedProcessId) loadReport('');
    else setData([]);
  }, [selectedProcessId, effectiveReportType, enrollmentFormat, questionnaireScope, questionnaireFormat, questionnaireIdentify, loadReport]);

  useEffect(() => {
    if (['homologation_signatures', 'enrollment_data', 'waiting_list_by_quota', 'questionnaire_socioeconomic', 'candidate_full_contact'].includes(apiReportType)) {
      setCampusFilter('all');
    }
  }, [effectiveReportType]);

  useEffect(() => {
    setContactSearch('');
    setContactSearchSubmitted('');
  }, [selectedProcessId]);

  useEffect(() => {
    if (apiReportType === 'questionnaire_socioeconomic') setQuestionnaireSearchSubmitted('');
  }, [effectiveReportType, apiReportType, questionnaireFormat, questionnaireScope, questionnaireIdentify, selectedProcessId]);

  const processName = processes.find(p => String(p.id) === selectedProcessId)?.name || '';

  const resolveReportPrintTitle = useCallback(() => {
    if (PROVA_GABARITO_REPORT_TYPES.includes(effectiveReportType)) {
      const gd = Array.isArray(data) && data[0] ? data[0] : null;
      if (gd?.pdf_filename) {
        return String(gd.pdf_filename).replace(/\.pdf$/i, '');
      }
    }
    const label =
      customTitle.trim() ||
      DEFAULT_TITLES[effectiveReportType] ||
      reportSelectOptions.find((o) => o.value === effectiveReportType)?.label?.replace(/^\d+\.\s*/, '') ||
      'Relatório';
    return buildReportExportFilename(label, processName, '');
  }, [effectiveReportType, data, customTitle, processName, reportSelectOptions]);

  const handlePrint = useCallback(() => {
    printWithDocumentTitle(resolveReportPrintTitle());
  }, [resolveReportPrintTitle]);

  const handleExportQuestionnaireCSV = useCallback(() => {
    const reportData = Array.isArray(data) && data[0] ? data[0] : null;
    if (!reportData) return;
    if (reportData.format === 'resumido') {
      const rows = [];
      const summary = reportData?.summary || {};
      for (const [, s] of Object.entries(summary)) {
        for (const [altId, count] of Object.entries(s.counts || {})) {
          if (count > 0) {
            rows.push({
              pergunta: s.question_text,
              alternativa: s.alternatives_by_id?.[altId] || altId,
              quantidade: count
            });
          }
        }
      }
      if (rows.length) exportToCSV(rows, 'questionario_socioeconomico_resumido.csv');
    } else {
      const allRows = reportData?.rows || [];
      const allKeys = new Set(['candidato']);
      allRows.forEach((r) => (r.answers || []).forEach((a) => allKeys.add(a.question_text || '')));
      const headers = ['candidato', ...(reportData?.questionnaire?.questions || []).map((q) => q.question_text).filter(Boolean)];
      const rows = allRows.map((r) => {
        const obj = { candidato: r.label };
        headers.forEach((h) => { obj[h] = ''; });
        (r.answers || []).forEach((a) => { if (a.question_text) obj[a.question_text] = a.answer; });
        return obj;
      });
      if (rows.length) exportToCSV(rows, 'questionario_socioeconomico_por_candidato.csv');
    }
  }, [data]);

  const isEnrollmentOrSignature = apiReportType === 'homologation_signatures' || apiReportType === 'enrollment_data';
  const showCampusFilter = isEnrollmentOrSignature || apiReportType === 'waiting_list_by_quota' || apiReportType === 'candidate_full_contact';
  const filteredGroups = isRoomSectionReport
    ? (data || [])
    : campusFilter === 'all'
      ? (data || [])
      : (data || []).filter((g) => g.campus === campusFilter);
  const campuses = isRoomSectionReport
    ? []
    : [...new Set((data || []).map((g) => g.campus).filter(Boolean))];

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const ids = new Set();
    filteredGroups.forEach((grp) => grp.courses?.forEach((cr) => cr.rows?.forEach((r) => r.application_id && ids.add(r.application_id))));
    setSelectedIds(ids);
  };

  const deselectAllVisible = () => {
    const ids = new Set();
    filteredGroups.forEach((grp) => grp.courses?.forEach((cr) => cr.rows?.forEach((r) => r.application_id && ids.add(r.application_id))));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const getSelectedRowsForSignature = () => {
    const rows = [];
    filteredGroups.forEach((grp) =>
      grp.courses?.forEach((cr) =>
        cr.rows?.forEach((r) => {
          if (r.application_id && selectedIds.has(r.application_id)) {
            rows.push({ ...r, campus: grp.campus, course: cr.course });
          }
        })
      )
    );
    return rows;
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="no-print" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24, alignItems: 'center' }}>
        <div>
          <label style={{ ...styles.label, marginRight: 8 }}>Processo</label>
          <select
            value={selectedProcessId}
            onChange={(e) => setSelectedProcessId(e.target.value)}
            style={styles.input}
          >
            <option value="">Selecione...</option>
            {processes.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ ...styles.label, marginRight: 8 }}>Relatório</label>
          <select
            value={effectiveReportType}
            onChange={(e) => setReportType(e.target.value)}
            style={styles.input}
          >
            {reportSelectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {apiReportType === 'questionnaire_socioeconomic' && (
          <>
            <div>
              <label style={{ ...styles.label, marginRight: 8 }}>Situação</label>
              <select value={questionnaireScope} onChange={(e) => setQuestionnaireScope(e.target.value)} style={styles.input}>
                <option value="todos">Todos (quem preencheu o questionário)</option>
                <option value="convocados">Convocados (dentro das vagas)</option>
                <option value="deferidos">Deferidos</option>
                <option value="indeferidos">Indeferidos</option>
                <option value="pendentes">Pendentes</option>
              </select>
            </div>
            <div>
              <label style={{ ...styles.label, marginRight: 8 }}>Formato</label>
              <select value={questionnaireFormat} onChange={(e) => setQuestionnaireFormat(e.target.value)} style={styles.input}>
                <option value="resumido">Resumido (contagem por alternativa)</option>
                <option value="por_candidato">Por candidato</option>
              </select>
            </div>
            {questionnaireFormat === 'por_candidato' && (
              <div>
                <label style={{ ...styles.label, marginRight: 8 }}>Identificação</label>
                <select value={questionnaireIdentify ? '1' : '0'} onChange={(e) => setQuestionnaireIdentify(e.target.value === '1')} style={styles.input}>
                  <option value="1">Identificado (nome e CPF)</option>
                  <option value="0">Anônimo</option>
                </select>
              </div>
            )}
            {questionnaireFormat === 'por_candidato' && (
              <div className="no-print" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ ...styles.label }}>Buscar por nome ou CPF:</label>
                  <input
                    type="text"
                    value={questionnaireSearch}
                    onChange={(e) => setQuestionnaireSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setQuestionnaireSearchSubmitted(questionnaireSearch.trim()))}
                    placeholder="Digite nome ou CPF e clique em Buscar"
                    style={{ ...styles.input, minWidth: 260 }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setQuestionnaireSearchSubmitted(questionnaireSearch.trim())}
                  style={{ ...styles.button, ...styles.buttonPrimary, padding: '8px 16px' }}
                >
                  Buscar
                </button>
              </div>
            )}
          </>
        )}
        {apiReportType === 'enrollment_data' && (
          <div>
            <label style={{ ...styles.label, marginRight: 8 }}>Formato</label>
            <select value={enrollmentFormat} onChange={(e) => setEnrollmentFormat(e.target.value)} style={styles.input}>
              <option value="resumo">Resumo (tabela)</option>
              <option value="completo">Dados completos</option>
            </select>
          </div>
        )}
        {showCampusFilter && (
          <div>
            <label style={{ ...styles.label, marginRight: 8 }}>Unidade</label>
            <select value={campusFilter} onChange={(e) => setCampusFilter(e.target.value)} style={styles.input}>
              <option value="all">Todos</option>
              {campuses.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
        {apiReportType === 'homologation_signatures' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" onClick={selectAllVisible} style={{ ...styles.button, padding: '6px 12px', fontSize: 13 }}>Selecionar todos</button>
            <button type="button" onClick={deselectAllVisible} style={{ ...styles.button, padding: '6px 12px', fontSize: 13 }}>Desmarcar todos</button>
            <button type="button" onClick={() => { setShowSignatureList(true); if (selectedIds.size > 0) setTimeout(() => document.getElementById('signature-list-print')?.scrollIntoView({ behavior: 'smooth' }), 150); }} style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 13 }}>Gerar lista de presença</button>
            {showSignatureList && selectedIds.size === 0 && (
              <span style={{ color: '#DC2626', fontSize: 13 }}>Selecione candidatos para gerar a lista.</span>
            )}
          </div>
        )}
        {effectiveReportType === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ ...styles.label }}>Buscar candidato (nome ou CPF)</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input
                type="text"
                value={candidateSearch}
                onChange={(e) => setCandidateSearch(e.target.value)}
                placeholder="Digite nome ou CPF para filtrar"
                style={{ ...styles.input, minWidth: 220 }}
              />
              <button onClick={() => loadReport(candidateSearch)} style={{ ...styles.button, ...styles.buttonPrimary, padding: '8px 16px' }}>Gerar</button>
            </div>
          </div>
        )}
        {effectiveReportType === 'candidate_full_contact' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={styles.label}>Buscar (opcional): nome, e-mail, CPF ou nº de inscrição (#32)</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setContactSearchSubmitted(contactSearch.trim()))}
                placeholder="Deixe vazio para listar todos os inscritos do processo"
                style={{ ...styles.input, minWidth: 280 }}
              />
              <button
                type="button"
                onClick={() => setContactSearchSubmitted(contactSearch.trim())}
                style={{ ...styles.button, ...styles.buttonPrimary, padding: '8px 16px' }}
              >
                Buscar
              </button>
            </div>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Ao imprimir ou «Salvar como PDF», o navegador sugere o nome: relatório + processo seletivo (ex.: LISTA PRELIMINAR… PROCESSO PROVA 2026 4).
            </span>
          </div>
        )}
        {(effectiveReportType === 'exemption_before' || effectiveReportType === 'exemption_after' || effectiveReportType === 'special_attendance_before' || effectiveReportType === 'special_attendance_after' || effectiveReportType === 'prova_preliminary_enrolled' || effectiveReportType === 'prova_homologation_enrollments' || effectiveReportType === 'prova_homologated_exam_places' || effectiveReportType === 'prova_homologated_exam_by_room' || effectiveReportType === 'prova_presence_by_room' || effectiveReportType === 'preliminary_list' || effectiveReportType === 'final_approved_list' || effectiveReportType === 'classification_preliminary' || effectiveReportType === 'classification_final' || PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType) || ['homologation_signatures', 'enrollment_data', 'waiting_list_by_quota', 'questionnaire_socioeconomic', 'candidate_full_contact'].includes(apiReportType) || PROVA_GABARITO_REPORT_TYPES.includes(effectiveReportType) || Object.keys(PROVA_INTERNAL_REPORT_MAP).includes(effectiveReportType)) && (
          <div>
            <label style={{ ...styles.label, marginRight: 8 }}>Título customizado (opcional)</label>
            <input
              type="text"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Deixe vazio para usar o padrão"
              style={{ ...styles.input, minWidth: 320 }}
            />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ ...styles.label, marginRight: 4 }}>Orientação:</label>
          <select value={printOrientation} onChange={(e) => setPrintOrientation(e.target.value)} style={{ ...styles.input, padding: '6px 10px', minWidth: 120 }}>
            <option value="portrait">Retrato</option>
            <option value="landscape">Paisagem</option>
          </select>
        </div>
        {apiReportType === 'questionnaire_socioeconomic' && (
          <button onClick={handleExportQuestionnaireCSV} style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 16px' }}>Exportar CSV</button>
        )}
        {effectiveReportType === 'candidate_full_contact' && (
          <button
            type="button"
            onClick={() => {
              const rows = [];
              filteredGroups.forEach((grp) =>
                grp.courses?.forEach((cr) =>
                  cr.rows?.forEach((r) => {
                    rows.push({
                      unidade: grp.campus,
                      curso: cr.course,
                      inscricao: r.application_id,
                      nome_social: r.name,
                      nome_registro: r.legal_name,
                      cpf: r.cpf,
                      email: r.email,
                      telefone: r.phone,
                      nascimento: r.birth_date,
                      mae: r.mother_name,
                      pai: r.father_name,
                      documento_tipo: r.identity_type,
                      documento_numero: r.identity_number,
                      orgao_emissor: r.issuing_org,
                      data_emissao_doc: r.issuing_date,
                      endereco: r.address
                    });
                  })
                )
              );
              if (rows.length) exportToCSV(rows, 'consulta_candidatos_contato.csv');
            }}
            style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 16px' }}
          >
            Exportar CSV
          </button>
        )}
        <button
          type="button"
          onClick={handlePrint}
          title={selectedProcessId ? `Nome sugerido ao salvar PDF: ${resolveReportPrintTitle()}.pdf` : undefined}
          style={{ ...styles.button, padding: '8px 16px' }}
        >
          Imprimir
        </button>
      </div>

      {error && <div style={{ color: '#DC2626', marginBottom: 16 }}>{error}</div>}
      {loading && <LoadingSpinner text="Carregando..." />}

      {!loading && selectedProcessId && (
        <div
          style={{ backgroundColor: 'white', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
          className={`report-content ${apiReportType === 'waiting_list_by_quota' ? 'report-waiting-list' : ''} ${apiReportType === 'candidate_full_contact' ? 'report-candidates-contact' : ''} ${apiReportType === 'homologation_signatures' && showSignatureList && selectedIds.size > 0 ? 'print-signature-only' : ''}`}
        >
          {effectiveReportType === 'history' && (
            <>
              <ReportHeader config={config} title="Histórico de alterações por candidato" processName={processName} />
              {data.length === 0 ? (
                <p style={{ color: '#6B7280' }}>
                  {candidateSearch.trim() ? 'Nenhum candidato encontrado com esse nome ou CPF.' : 'Nenhuma alteração registrada neste processo.'}
                </p>
              ) : (
                data.map((item) => (
                  <div key={item.user_id} style={{ marginBottom: 24, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{item.user?.name || 'Candidato'}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>CPF: {item.user?.cpf || '-'}</div>
                    {item.course_changes?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>Trocas de curso:</div>
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                          <thead><tr><th style={{ textAlign: 'left', padding: 4 }}>De</th><th style={{ textAlign: 'left', padding: 4 }}>Para</th><th style={{ textAlign: 'left', padding: 4 }}>Data</th><th style={{ textAlign: 'left', padding: 4 }}>IP</th></tr></thead>
                          <tbody>
                            {item.course_changes.map((c, i) => (
                              <tr key={i}><td style={{ padding: 4 }}>{c.old_course}</td><td style={{ padding: 4 }}>{c.new_course}</td><td style={{ padding: 4 }}>{formatDate(c.created_at)}</td><td style={{ padding: 4 }}>{c.ip_address || '—'}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {item.quota_changes?.length > 0 && (
                      <div>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>Alterações de cota:</div>
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                          <thead><tr><th style={{ textAlign: 'left', padding: 4 }}>De</th><th style={{ textAlign: 'left', padding: 4 }}>Para</th><th style={{ textAlign: 'left', padding: 4 }}>Data</th><th style={{ textAlign: 'left', padding: 4 }}>IP</th></tr></thead>
                          <tbody>
                            {item.quota_changes.map((q, i) => (
                              <tr key={i}><td style={{ padding: 4 }}>{q.from_quota}</td><td style={{ padding: 4 }}>{q.to_quota}</td><td style={{ padding: 4 }}>{formatDate(q.created_at)}</td><td style={{ padding: 4 }}>{q.ip_address || '—'}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))
              )}
            </>
          )}

          {effectiveReportType === 'candidate_full_contact' && (
            <>
              <ReportHeader
                config={config}
                title={customTitle.trim() || DEFAULT_TITLES.candidate_full_contact}
                processName={processName}
              />
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: -12, marginBottom: 16 }}>
                Todos os inscritos do processo (inclui deferidos, indeferidos e demais situações). CPF completo — tratar com confidencialidade.
              </p>
              {!filteredGroups || filteredGroups.length === 0 ? (
                <p style={{ color: '#6B7280' }}>
                  {contactSearchSubmitted ? 'Nenhum candidato encontrado com esse filtro.' : 'Nenhuma inscrição neste processo.'}
                </p>
              ) : (
                filteredGroups.map((grp, gi) => (
                  <div key={gi} style={{ marginBottom: 24, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h3>
                    {grp.courses?.map((cr, ci) => (
                      <div key={ci} style={{ marginBottom: ci < (grp.courses?.length || 0) - 1 ? 20 : 0 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{cr.course}</h4>
                        <div
                          className="report-waiting-list-scroll report-candidates-contact-scroll"
                          style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%', maxWidth: '100%' }}
                        >
                          <table className="report-candidates-contact-table" style={REPORT_TABLE_LAYOUT.contactTable}>
                            <colgroup>
                              {REPORT_TABLE_LAYOUT.contactColPct.map((w, wi) => (
                                <col key={wi} style={{ width: w }} />
                              ))}
                            </colgroup>
                            <thead>
                              <tr style={{ backgroundColor: '#F3F4F6' }}>
                                {['Nº', 'Nome', 'CPF', 'E-mail', 'Tel.', 'Nasc.', 'Mãe', 'Pai', 'Doc.', 'End.'].map((h) => (
                                  <th
                                    key={h}
                                    style={{
                                      textAlign: 'left',
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      verticalAlign: 'bottom',
                                      fontSize: 9,
                                      lineHeight: 1.15,
                                      fontWeight: 600,
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {cr.rows?.map((row, ri) => (
                                <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                  <td
                                    className="contact-cell-nowrap"
                                    style={{ padding: '4px 5px', border: '1px solid #E5E7EB', fontSize: 9, fontWeight: 600, verticalAlign: 'top' }}
                                  >
                                    #{row.application_id}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                    title={row.name}
                                  >
                                    {row.name}
                                  </td>
                                  <td
                                    className="contact-cell-nowrap"
                                    style={{ padding: '4px 5px', border: '1px solid #E5E7EB', fontSize: 9, verticalAlign: 'top' }}
                                  >
                                    {row.cpf}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {row.email || '—'}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {row.phone || '—'}
                                  </td>
                                  <td
                                    className="contact-cell-nowrap"
                                    style={{ padding: '4px 5px', border: '1px solid #E5E7EB', fontSize: 9, verticalAlign: 'top' }}
                                  >
                                    {row.birth_date ? formatDate(row.birth_date) : '—'}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                    title={row.mother_name}
                                  >
                                    {row.mother_name || '—'}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                    title={row.father_name}
                                  >
                                    {row.father_name || '—'}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {[row.identity_type, row.identity_number].filter(Boolean).join(' ') || '—'}
                                    {row.issuing_org ? ` / ${row.issuing_org}` : ''}
                                    {row.issuing_date ? ` (${formatDate(row.issuing_date)})` : ''}
                                  </td>
                                  <td
                                    style={{
                                      padding: '4px 5px',
                                      border: '1px solid #E5E7EB',
                                      fontSize: 9,
                                      verticalAlign: 'top',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.2,
                                    }}
                                    title={row.address}
                                  >
                                    {row.address || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </>
          )}

          {(effectiveReportType === 'classification_preliminary' ||
            effectiveReportType === 'classification_final' ||
            PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType)) && (
            <>
              <ReportHeader
                config={config}
                title={customTitle.trim() || DEFAULT_TITLES[effectiveReportType]}
                processName={processName}
              />
              {PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType) && (
                <p style={{ fontSize: 13, color: '#4B5563', marginBottom: 16, lineHeight: 1.5 }}>
                  {selectedProcess?.exam_uses_essay ? (
                    <>
                      Com redação: primeiro o <strong>corte pela nota objetiva</strong> (multiplicador ×
                      vagas por grupo — ampla concorrência e cotas); depois classificação por{' '}
                      <strong>nota total</strong> (objetiva + redação), precedência e remanejamento.
                    </>
                  ) : (
                    <>
                      Classificação por <strong>nota objetiva</strong>, precedência e remanejamento
                      (sem corte — quem não ocupa vaga vai para lista de espera).
                    </>
                  )}
                </p>
              )}
              {!data || data.length === 0 ? (
                <p style={{ color: '#6B7280' }}>
                  {isOrdemProcess && !processUsesQuotas
                    ? 'Nenhum inscrito deferido encontrado para classificação por ordem de inscrição.'
                    : 'Nenhum registro encontrado ou processo sem cotas/vagas configuradas.'}
                </p>
              ) : (
                data.map((grp, gi) => {
                  const isProvaClassReport = PROVA_CLASSIFICATION_REPORT_TYPES.includes(effectiveReportType);
                  const hasEssayScores = isProvaClassReport && selectedProcess?.exam_uses_essay;
                  const classCol = isProvaClassReport
                    ? (hasEssayScores
                      ? REPORT_TABLE_LAYOUT.provaClassificationColEssay
                      : REPORT_TABLE_LAYOUT.provaClassificationColNoEssay)
                    : REPORT_TABLE_LAYOUT.classificationCol;
                  const tableStyle = isProvaClassReport
                    ? REPORT_TABLE_LAYOUT.provaClassificationTable
                    : REPORT_TABLE_LAYOUT.table;
                  const thBase = isProvaClassReport
                    ? REPORT_TABLE_LAYOUT.provaClassificationTh
                    : { textAlign: 'left', padding: 8, border: '1px solid #E5E7EB' };
                  const tdBase = isProvaClassReport
                    ? REPORT_TABLE_LAYOUT.provaClassificationCell
                    : { padding: 8, border: '1px solid #E5E7EB' };
                  const scoreTd = isProvaClassReport
                    ? { ...tdBase, textAlign: 'right', whiteSpace: 'nowrap' }
                    : { ...tdBase };
                  return (
                  <div key={gi} style={{ marginBottom: 32 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h3>
                    {grp.courses?.map((cr, ci) => (
                      <div key={ci} style={{ marginBottom: 24, marginLeft: 8 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{cr.course}</h4>
                        <table
                          className={isProvaClassReport ? 'report-prova-classification-table' : undefined}
                          style={tableStyle}
                        >
                          <thead>
                            <tr style={{ backgroundColor: '#F3F4F6' }}>
                              {isProvaClassReport && (
                                <th className="report-col-num" style={{ ...thBase, textAlign: 'center', width: classCol.pos, whiteSpace: 'nowrap' }}>Pos.</th>
                              )}
                              <th className={isProvaClassReport ? 'report-col-num' : undefined} style={{ ...thBase, textAlign: 'left', width: classCol.num }}>Nº</th>
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.name }}>Nome</th>
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.cpf }}>CPF</th>
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.campus }}>Unidade</th>
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.course }}>Curso</th>
                              {showClassificationQuota && (
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.quotaInsc }}>
                                {isProvaClassReport ? 'Cota insc.' : 'Cota da inscrição'}
                              </th>
                              )}
                              {hasEssayScores && (
                                <>
                                  <th className="report-col-score" style={{ ...thBase, textAlign: 'right', width: classCol.notaObj, whiteSpace: 'nowrap' }}>Obj.</th>
                                  <th className="report-col-score" style={{ ...thBase, textAlign: 'right', width: classCol.notaRed, whiteSpace: 'nowrap' }}>Red.</th>
                                </>
                              )}
                              {showClassificationScoreCol && (
                              <th className={isProvaClassReport ? 'report-col-score' : undefined} style={{ ...thBase, textAlign: isProvaClassReport ? 'right' : 'left', width: classCol.total || classCol.score, whiteSpace: isProvaClassReport ? 'nowrap' : undefined }}>
                                {isProvaClassReport ? 'Total' : 'Nota'}
                              </th>
                              )}
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.situacao }}>Situação</th>
                              {showClassificationQuota && (
                              <th style={{ ...thBase, textAlign: 'left', width: classCol.quotaClass }}>
                                {isProvaClassReport ? 'Cota class.' : 'Cota da classificação'}
                              </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {cr.rows?.map((row, ri) => (
                              <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                {isProvaClassReport && (
                                  <td className="report-col-num" style={{ ...tdBase, textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.num ?? ri + 1}</td>
                                )}
                                <td className={isProvaClassReport ? 'report-col-num' : undefined} style={{ ...tdBase, fontWeight: 600, color: '#374151', whiteSpace: isProvaClassReport ? 'nowrap' : undefined }}>{row.application_id != null ? `#${row.application_id}` : '-'}</td>
                                <td style={{ ...tdBase, overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }} title={row.name}>{row.name}</td>
                                <td style={{ ...tdBase, whiteSpace: isProvaClassReport ? 'nowrap' : undefined }}>{row.cpf}</td>
                                <td style={{ ...tdBase, wordBreak: 'break-word' }}>{row.campus || '-'}</td>
                                <td style={{ ...tdBase, overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }} title={row.course}>{row.course}</td>
                                {showClassificationQuota && (
                                <td style={{ ...tdBase, whiteSpace: isProvaClassReport ? 'nowrap' : undefined }}>{row.quota_inscricao || '-'}</td>
                                )}
                                {hasEssayScores && (
                                  <>
                                    <td className="report-col-score" style={scoreTd}>
                                      {row.nota_objetiva != null ? Number(row.nota_objetiva).toFixed(2) : '-'}
                                    </td>
                                    <td className="report-col-score" style={scoreTd}>
                                      {row.nota_redacao != null ? Number(row.nota_redacao).toFixed(2) : '—'}
                                    </td>
                                  </>
                                )}
                                {showClassificationScoreCol && (
                                <td className={isProvaClassReport ? 'report-col-score' : undefined} style={scoreTd}>
                                  {row.score != null ? Number(row.score).toFixed(2) : '-'}
                                </td>
                                )}
                                <td style={{ ...tdBase, wordBreak: 'break-word' }}>{row.situacao || '-'}</td>
                                {showClassificationQuota && (
                                <td style={{ ...tdBase, whiteSpace: isProvaClassReport ? 'nowrap' : undefined }}>{row.quota_classificacao || '-'}</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {cr.precedence_summary && (
                          <div style={{ marginTop: 12, padding: 12, backgroundColor: '#F9FAFB', borderRadius: 8, fontSize: 12, color: '#4B5563' }}>
                            <strong>Resumo de precedência/remanejamento:</strong> {cr.precedence_summary}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  );
                })
              )}
            </>
          )}

          {apiReportType === 'homologation_signatures' && (
            <>
              <ReportHeader config={config} title="Lista de presença" processName={processName} />
              <div className="report-homologation-full">
                {!filteredGroups || filteredGroups.length === 0 ? (
                  <p style={{ color: '#6B7280' }}>Nenhum candidato classificado ou processo sem cotas configuradas.</p>
                ) : (
                  filteredGroups.map((grp, gi) => (
                    <div key={gi} style={{ marginBottom: 24, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h3>
                      {grp.courses?.map((cr, ci) => (
                        <div key={ci} style={{ marginBottom: ci < (grp.courses?.length || 0) - 1 ? 20 : 0 }}>
                          <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{cr.course}</h4>
                          <table style={REPORT_TABLE_LAYOUT.table}>
                            <thead>
                              <tr style={{ backgroundColor: '#F3F4F6' }}>
                                <th className="hide-on-print" style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.checkbox }} />
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.id }}>Nº</th>
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.num }}>Pos.</th>
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.name }}>Nome</th>
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.cpf }}>CPF</th>
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.signature }}>Assinatura</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cr.rows?.map((row, ri) => (
                                <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                  <td className="hide-on-print" style={{ padding: 8, border: '1px solid #E5E7EB' }}>
                                    <input type="checkbox" checked={selectedIds.has(row.application_id)} onChange={() => toggleSelect(row.application_id)} />
                                  </td>
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB', fontWeight: 600, color: '#374151' }}>{row.application_id != null ? `#${row.application_id}` : '-'}</td>
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.num}</td>
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.name}>{row.name}</td>
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.cpf}</td>
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.signature || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
              {showSignatureList && selectedIds.size > 0 && (
                <div id="signature-list-print" style={{ marginTop: 32, padding: 24, border: '1px solid #E5E7EB', borderRadius: 8, backgroundColor: '#FAFAFA' }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Lista de presença (selecionados)</h3>
                  {filteredGroups.map((grp, gi) => {
                    const rows = getSelectedRowsForSignature().filter((r) => r.campus === grp.campus);
                    if (rows.length === 0) return null;
                    const byCourse = {};
                    rows.forEach((r) => { byCourse[r.course] = (byCourse[r.course] || []).concat(r); });
                    return (
                      <div key={gi} style={{ marginBottom: 24, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8, backgroundColor: 'white' }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h4>
                        {Object.entries(byCourse).map(([course, courseRows]) => (
                          <div key={course} style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{course}</div>
                            <table style={REPORT_TABLE_LAYOUT.table}>
                              <thead>
                                <tr style={{ backgroundColor: '#F3F4F6' }}>
                                  <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.id }}>Nº</th>
                                  <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.name }}>Nome</th>
                                  <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.cpf }}>CPF</th>
                                  <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.col.signature }}>Assinatura</th>
                                </tr>
                              </thead>
                              <tbody>
                                {courseRows.map((r, i) => (
                                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                    <td style={{ padding: 8, border: '1px solid #E5E7EB', fontWeight: 600, color: '#374151' }}>{r.application_id != null ? `#${r.application_id}` : '-'}</td>
                                    <td style={{ padding: 8, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.name}>{r.name}</td>
                                    <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{r.cpf}</td>
                                    <td style={{ padding: 8, border: '1px solid #E5E7EB' }} />
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {apiReportType === 'enrollment_data' && (
            <>
              <ReportHeader config={config} title="Lista de Convocação para Matrícula" processName={processName} />
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 4, marginBottom: 16 }}>
                Este relatório exibe apenas os candidatos convocados. Para candidatos em lista de espera, use o relatório «Lista de espera (interno)» no menu Relatórios.
              </p>
              {!filteredGroups || filteredGroups.length === 0 ? (
                <p style={{ color: '#6B7280' }}>
                  {isOrdemProcess && !processUsesQuotas
                    ? 'Nenhum candidato convocado encontrado.'
                    : 'Nenhum candidato classificado ou processo sem cotas/vagas configuradas.'}
                </p>
              ) : (
                filteredGroups.map((grp, gi) => (
                  <div key={gi} style={{ marginBottom: 24, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h3>
                    {grp.courses?.map((cr, ci) => (
                      <div key={ci} style={{ marginBottom: ci < (grp.courses?.length || 0) - 1 ? 20 : 0 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{cr.course}</h4>
                        {cr.format === 'completo' ? (
                          cr.rows?.map((row, ri) => (
                            <div key={ri} style={{ marginBottom: 16, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>{row.num}. {row.name}</div>
                                <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                                  <div>CPF: {row.cpf}</div>
                                  <div>Nascimento: {row.birth_date || '-'}</div>
                                  <div>Mãe: {row.mother_name || '-'}</div>
                                  <div>Pai: {row.father_name || '-'}</div>
                                  <div>RG/CNH: {row.identity_type} {row.identity_number}</div>
                                  <div>Órgão: {row.issuing_org || '-'}</div>
                                  <div style={{ gridColumn: '1 / -1' }}>Endereço: {row.address || '-'}</div>
                                  <div>Telefone: {row.phone || '-'}</div>
                                  <div>E-mail: {row.email || '-'}</div>
                                  <div>Curso: {row.course}</div>
                                  {showEnrollmentQuota && (
                                  <>
                                  <div>Cota inscrição: {row.quota_inscricao}</div>
                                  <div>Cota classificação: {row.quota_classificacao}</div>
                                  </>
                                  )}
                                  {showEnrollmentNota && (
                                  <div>Nota: {row.score != null ? Number(row.score).toFixed(2) : '-'}</div>
                                  )}
                                  <div>2ª chamada: {row.segunda_chamada || ''}</div>
                                  <div>3ª chamada: {row.terceira_chamada || ''}</div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <table className="report-enrollment-table" style={{ ...REPORT_TABLE_LAYOUT.table, fontSize: 11 }}>
                            <thead>
                              <tr style={{ backgroundColor: '#F3F4F6' }}>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.id, whiteSpace: 'nowrap' }}>Nº</th>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.num, whiteSpace: 'nowrap' }}>Pos.</th>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.name, whiteSpace: 'nowrap' }}>Nome</th>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.cpf, whiteSpace: 'nowrap' }}>CPF</th>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.course, whiteSpace: 'nowrap' }}>Curso</th>
                                {showEnrollmentQuota && (
                                <>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.quotaInsc, whiteSpace: 'nowrap' }}>Cota insc.</th>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.quotaClass, whiteSpace: 'nowrap' }}>Cota class.</th>
                                </>
                                )}
                                {showEnrollmentNota && (
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.score, whiteSpace: 'nowrap' }}>Nota</th>
                                )}
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.chamada, whiteSpace: 'nowrap' }}>2ª ch.</th>
                                <th style={{ textAlign: 'left', padding: 6, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.enrollmentCol.chamada, whiteSpace: 'nowrap' }}>3ª ch.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cr.rows?.map((row, ri) => (
                                <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB', fontWeight: 600, color: '#374151' }}>{row.application_id != null ? `#${row.application_id}` : '-'}</td>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.num}</td>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }} title={row.name}>{row.name}</td>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.cpf}</td>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }} title={row.course}>{row.course}</td>
                                  {showEnrollmentQuota && (
                                  <>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.quota_inscricao}</td>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.quota_classificacao}</td>
                                  </>
                                  )}
                                  {showEnrollmentNota && (
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.score != null ? Number(row.score).toFixed(2) : '-'}</td>
                                  )}
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.segunda_chamada || ''}</td>
                                  <td style={{ padding: 6, border: '1px solid #E5E7EB' }}>{row.terceira_chamada || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </>
          )}

          {apiReportType === 'waiting_list_by_quota' && (
            <>
              <ReportHeader config={config} title="Lista de espera" processName={processName} />
              {!filteredGroups || filteredGroups.length === 0 ? (
                <p style={{ color: '#6B7280' }}>Nenhum candidato em lista de espera ou processo sem cotas configuradas.</p>
              ) : (
                filteredGroups.map((grp, gi) => (
                  <div key={gi} style={{ marginBottom: 24, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h3>
                    {grp.courses?.map((cr, ci) => (
                      <div key={ci} style={{ marginBottom: ci < (grp.courses?.length || 0) - 1 ? 20 : 0 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{cr.course}</h4>
                        <div className="report-waiting-list-scroll" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
                          <table className="report-waiting-list-table" style={REPORT_TABLE_LAYOUT.waitingListTable}>
                            <thead>
                              <tr style={{ backgroundColor: '#F3F4F6' }}>
                                <th style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.id, textAlign: 'left' }}>Nº inscrição</th>
                                <th style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.num, textAlign: 'left' }}>Pos.</th>
                                <th style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.name, textAlign: 'left' }}>Nome</th>
                                <th style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.cpf, textAlign: 'left' }}>CPF</th>
                                {showEnrollmentNota && (
                                <th style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.score, textAlign: 'left' }}>Nota</th>
                                )}
                                {showEnrollmentQuota && (
                                <th style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.quotaInsc, textAlign: 'left' }}>Cota inscrição</th>
                                )}
                                {cr.quota_codes?.map((code) => (
                                  <th key={code} style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.quotaCode, textAlign: 'left' }}>{code}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {cr.rows?.map((row, ri) => (
                                <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                  <td style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.id, fontWeight: 600, color: '#374151' }}>{row.application_id != null ? `#${row.application_id}` : '-'}</td>
                                  <td style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.num }}>{row.num}</td>
                                  <td style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.name }} title={row.name}>{row.name || '—'}</td>
                                  <td style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.cpf }}>{row.cpf}</td>
                                  {showEnrollmentNota && (
                                  <td style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.score }}>{row.score != null ? Number(row.score).toFixed(2) : '-'}</td>
                                  )}
                                  {showEnrollmentQuota && (
                                  <td style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.quotaInsc }}>{row.quota_inscricao}</td>
                                  )}
                                  {cr.quota_codes?.map((code) => (
                                    <td key={code} style={{ ...REPORT_TABLE_LAYOUT.waitingListThTd.base, ...REPORT_TABLE_LAYOUT.waitingListThTd.quotaCode }}>{row[`pos_${code}`] || '-'}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </>
          )}

          {apiReportType === 'questionnaire_socioeconomic' && (() => {
            const reportData = Array.isArray(data) && data[0] ? data[0] : null;
            if (!reportData) return <p style={{ color: '#6B7280' }}>Nenhum dado. Verifique se o processo possui questionário vinculado e candidatos na situação selecionada.</p>;
            const title = customTitle.trim() || `Questionário Socioeconômico - ${reportData.scope === 'convocados' ? 'Convocados' : 'Resultado Final'}`;
            return (
              <>
                <ReportHeader config={config} title={title} processName={processName} />
                <p style={{ fontSize: 12, color: '#6B7280', marginTop: 4, marginBottom: 16 }}>
                  {reportData.questionnaire?.name} • {reportData.total_candidates} candidato(s)
                  {reportData.format === 'por_candidato' && ` • ${reportData.identify ? 'Identificado' : 'Anônimo'}`}
                </p>
                {reportData.format === 'por_candidato' && reportData.search_required ? (
                  <p style={{ color: '#6B7280' }}>Digite o nome ou CPF do candidato e clique em Buscar para ver o questionário.</p>
                ) : reportData.total_candidates === 0 ? (
                  <p style={{ color: '#6B7280' }}>
                    {reportData.format === 'resumido' ? 'Nenhum candidato na situação selecionada respondeu ao questionário.' : 'Nenhum candidato encontrado com esse nome ou CPF.'}
                  </p>
                ) : reportData.format === 'resumido' ? (
                  <div style={{ marginBottom: 24, fontSize: 13 }}>
                    {(reportData.questionnaire?.questions || []).map((q, qi) => {
                      const counts = reportData.summary?.[q.id]?.counts || {};
                      const parts = (q.alternatives || []).filter((alt) => (counts[alt.id] || 0) > 0).map((alt) => `${alt.alternative_text}: ${counts[alt.id]}`);
                      return (
                        <div key={qi} style={{ marginBottom: 10, padding: '10px 12px', borderLeft: '3px solid #3B82F6', backgroundColor: '#F9FAFB', borderRadius: 4 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>{q.question_text}</div>
                          <div style={{ color: '#4B5563', fontSize: 12 }}>{parts.length ? parts.join(' • ') : 'Sem respostas'}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ marginBottom: 24 }}>
                    {(() => {
                      const row = reportData.rows?.[0];
                      if (!row) return (
                        <p style={{ color: '#6B7280' }}>
                          {reportData.search_required ? 'Digite o nome ou CPF do candidato e clique em Buscar para ver o questionário.' : 'Nenhum candidato encontrado com esse nome ou CPF.'}
                        </p>
                      );
                      const showId = reportData.identify; // Sempre mostrar identificação quando formato é por candidato + identificado
                      return (
                        <div style={{ padding: 16, border: '1px solid #E5E7EB', borderRadius: 8, backgroundColor: '#F9FAFB' }} className="questionnaire-candidate-print">
                          {showId ? (
                            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8 }}>
                              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Identificação do candidato</div>
                              <div style={{ fontSize: 13 }}><strong>Nome:</strong> {row.name || '-'}</div>
                              <div style={{ fontSize: 13 }}><strong>CPF:</strong> {row.cpf || '-'}</div>
                            </div>
                          ) : (
                            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>{row.label}</div>
                          )}
                          {row.answers?.length === 0 ? (
                            <p style={{ color: '#6B7280', fontSize: 13 }}>Nenhuma resposta registrada.</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                              {(row.answers || []).map((a, ai) => (
                                <div key={ai} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: ai < (row.answers?.length || 0) - 1 ? '1px solid #E5E7EB' : 'none' }}>
                                  <span style={{ color: '#6B7280', flex: '0 0 45%' }}>{a.question_text}</span>
                                  <span style={{ fontWeight: 500 }}>{a.answer}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            );
          })()}

          {(effectiveReportType === 'exemption_before' || effectiveReportType === 'exemption_after' || effectiveReportType === 'special_attendance_before' || effectiveReportType === 'special_attendance_after' || effectiveReportType === 'prova_preliminary_enrolled' || effectiveReportType === 'prova_homologation_enrollments' || effectiveReportType === 'prova_homologated_exam_places' || effectiveReportType === 'preliminary_list' || effectiveReportType === 'final_approved_list') && (
            <>
              <ReportHeader
                config={config}
                title={customTitle.trim() || DEFAULT_TITLES[effectiveReportType]}
                processName={processName}
              />
              {!data || data.length === 0 ? (
                <p style={{ color: '#6B7280' }}>Nenhum registro encontrado.</p>
              ) : (
                data.map((grp, gi) => (
                  <div key={gi} style={{ marginBottom: 32 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#374151' }}>{grp.campus}</h3>
                    {grp.courses?.map((cr, ci) => (
                      <div key={ci} style={{ marginBottom: 24, marginLeft: 8 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{cr.course}</h4>
                        <table style={REPORT_TABLE_LAYOUT.table}>
                          <thead>
                            <tr style={{ backgroundColor: '#F3F4F6' }}>
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.num }}>Nº</th>
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.name }}>Nome</th>
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.cpf }}>CPF</th>
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.campus }}>Unidade</th>
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.course }}>Curso</th>
                              {showListReportQuota && (
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.quota }}>Cota</th>
                              )}
                              {effectiveReportType === 'prova_homologated_exam_places' && (
                                <>
                                  <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: '11%' }}>Local da prova</th>
                                  <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: '8%' }}>Sala</th>
                                </>
                              )}
                              {showListReportNota && (
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.score }}>Nota</th>
                              )}
                              {effectiveReportType !== 'final_approved_list' && effectiveReportType !== 'prova_homologation_enrollments' && effectiveReportType !== 'prova_homologated_exam_places' && (
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.situacao }}>Situação</th>
                              )}
                              {(effectiveReportType === 'exemption_before' || effectiveReportType === 'special_attendance_before' || effectiveReportType === 'preliminary_list' || effectiveReportType === 'prova_preliminary_enrolled') && (
                                <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.listCol.motivo }}>Motivo</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {cr.rows?.map((row, ri) => (
                              <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                <td style={{ padding: 8, border: '1px solid #E5E7EB', fontWeight: 600, color: '#374151' }}>{row.application_id != null ? `#${row.application_id}` : '-'}</td>
                                <td style={{ padding: 8, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.name}>{row.name}</td>
                                <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.cpf}</td>
                                <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.campus || '-'}</td>
                                <td style={{ padding: 8, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.course}>{row.course}</td>
                                {showListReportQuota && (
                                <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.quota || '-'}</td>
                                )}
                                {effectiveReportType === 'prova_homologated_exam_places' && (
                                  <>
                                    <td style={{ padding: 8, border: '1px solid #E5E7EB', wordBreak: 'break-word' }} title={row.exam_local}>{row.exam_local || '—'}</td>
                                    <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.exam_sala || '—'}</td>
                                  </>
                                )}
                                {showListReportNota && (
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>
                                    {row.score != null ? Number(row.score).toFixed(2) : '-'}
                                  </td>
                                )}
                                {effectiveReportType !== 'final_approved_list' && effectiveReportType !== 'prova_homologation_enrollments' && effectiveReportType !== 'prova_homologated_exam_places' && (
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>
                                    {effectiveReportType === 'prova_preliminary_enrolled'
                                      ? (row.situacao || '-')
                                      : ((effectiveReportType === 'preliminary_list' ? STATUS_LABELS : EXEMPTION_LABELS)[row.status || row.exemption_status] || row.status || row.exemption_status || '-')}
                                  </td>
                                )}
                                {(effectiveReportType === 'exemption_before' || effectiveReportType === 'special_attendance_before' || effectiveReportType === 'preliminary_list' || effectiveReportType === 'prova_preliminary_enrolled') && (
                                  <td style={{ padding: 8, border: '1px solid #E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.reason}>{row.reason || '-'}</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </>
          )}

          {isRoomSectionReport && (
            <>
              <ReportHeader
                config={config}
                title={customTitle.trim() || DEFAULT_TITLES[effectiveReportType]}
                processName={processName}
              />
              {effectiveReportType === 'prova_presence_by_room' && (
                <p style={{ fontSize: 12, color: '#6B7280', marginTop: -8, marginBottom: 16 }}>
                  Uso interno — CPF completo para conferência e assinatura. Tratar com confidencialidade.
                </p>
              )}
              {effectiveReportType === 'prova_homologated_exam_by_room' && (
                <p style={{ fontSize: 12, color: '#6B7280', marginTop: -8, marginBottom: 16 }}>
                  Uma folha por local/sala — CPF mascarado. Imprimir e afixar na porta da sala.
                </p>
              )}
              {!data || data.length === 0 ? (
                <p style={{ color: '#6B7280' }}>Nenhum inscrito homologado ou sem agrupamento por local/sala.</p>
              ) : (
                <div className="report-prova-room-sections">
                  {data.map((sec, si) => (
                    <div key={si} className="report-prova-room-block" style={{ marginBottom: 28, paddingBottom: 8, borderBottom: '1px solid #E5E7EB' }}>
                      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#111827' }}>
                        <span style={{ color: '#6B7280' }}>Local:</span> {sec.exam_local || '—'}{' '}
                        <span style={{ color: '#6B7280', marginLeft: 12 }}>Sala:</span> {sec.exam_sala || '—'}
                      </h3>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9CA3AF' }}>
                        {sec.rows?.length || 0} candidato(s) — inscrições homologadas (pagamento/iseno)
                      </p>
                      <table style={{ ...REPORT_TABLE_LAYOUT.table, fontSize: 12 }}>
                        <thead>
                          <tr style={{ backgroundColor: '#F3F4F6' }}>
                            <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: effectiveReportType === 'prova_presence_by_room' ? REPORT_TABLE_LAYOUT.roomPresenceCol.num : '5%' }}>Nº</th>
                            {effectiveReportType === 'prova_presence_by_room' && (
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.roomPresenceCol.inscription }}>Inscrição</th>
                            )}
                            <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: effectiveReportType === 'prova_presence_by_room' ? REPORT_TABLE_LAYOUT.roomPresenceCol.name : '28%' }}>Nome</th>
                            <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: effectiveReportType === 'prova_presence_by_room' ? REPORT_TABLE_LAYOUT.roomPresenceCol.cpf : '16%' }}>CPF</th>
                            <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: effectiveReportType === 'prova_presence_by_room' ? REPORT_TABLE_LAYOUT.roomPresenceCol.campus : '12%' }}>Unidade</th>
                            <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: effectiveReportType === 'prova_presence_by_room' ? REPORT_TABLE_LAYOUT.roomPresenceCol.course : undefined }}>Curso</th>
                            {effectiveReportType === 'prova_presence_by_room' && (
                              <th style={{ textAlign: 'left', padding: 8, border: '1px solid #E5E7EB', width: REPORT_TABLE_LAYOUT.roomPresenceCol.signature, minWidth: REPORT_TABLE_LAYOUT.roomPresenceCol.signatureMin }}>Assinatura do candidato</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {(sec.rows || []).map((row, ri) => (
                            <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'white' : '#F9FAFB' }}>
                              <td style={{ padding: 8, border: '1px solid #E5E7EB', fontWeight: 600 }}>{row.num}</td>
                              {effectiveReportType === 'prova_presence_by_room' && (
                                <td style={{ padding: 8, border: '1px solid #E5E7EB', fontWeight: 600, color: '#374151' }}>#{row.application_id}</td>
                              )}
                              <td style={{ padding: 8, border: '1px solid #E5E7EB', wordBreak: 'break-word' }} title={row.name}>{row.name}</td>
                              <td style={{ padding: 8, border: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{row.cpf}</td>
                              <td style={{ padding: 8, border: '1px solid #E5E7EB' }}>{row.campus || '—'}</td>
                              <td style={{ padding: 8, border: '1px solid #E5E7EB', wordBreak: 'break-word' }} title={row.course}>{row.course}</td>
                              {effectiveReportType === 'prova_presence_by_room' && (
                                <td style={{ padding: 8, border: '1px solid #E5E7EB', height: 36, minHeight: 36 }} />
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {PROVA_GABARITO_REPORT_TYPES.includes(effectiveReportType) && (
            <GabaritoReportPreview
              reportData={Array.isArray(data) && data[0] ? data[0] : null}
              config={config}
              customTitle={customTitle}
              processName={processName}
              error={error}
            />
          )}

        </div>
      )}
    </div>
  );
});
